import React, { useEffect, useState } from "react";
import {
  List,
  Typography,
  Space,
  Pagination,
  Col,
  Row,
} from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  DatabaseOutlined,
  ArrowRightOutlined,
  FolderOpenOutlined
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { apiCaller } from "src/util/apiCaller";
import SearchForm from "../SearchForm";
import { useLocation } from "react-router-dom";

const { Title, Paragraph, Text } = Typography;

interface HistoricalDocument {
  idTulieu: number;
  maDanToc: string;
  idNhomTuLieu: number;
  idLoaiTuLieu: number;
  anhDaiDien: string;
  isTuLieu: boolean;
  isVideo: boolean;
  isHinhAnh: boolean;
  tenTuLieu: string;
  moTa: string;
  tacGia: string;
  ngayDang: string;
  ngayBatDauLT: string;
  ngayKetThucLT: string;
  historicalPeriod: string;
  trangThai: number;
  isSuDung: boolean;
}

const ParentTuLieu: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<HistoricalDocument[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const location = useLocation();
  const tenNhomTuLieu = location.state?.tenNhomTuLieu;
  const idNhomTuLieuState = location.state?.idNhomTuLieu;

  const [searchParams, setSearchParams] = useState({
    nhom: "",
    loai: "",
    keyword: "",
  });

  const fetchHistoricalDocuments = async (
    page: number,
    pageSize: number,
    idNhomTuLieuState: string,
    idLoaiTuLieu: string,
    keyword: string
  ) => {
    setLoading(true);
    try {
      const response = await apiCaller({
        url: `/apipublic/tulieuhienthis?istulieu=true&idnhomtulieu=${
          idNhomTuLieuState || ""
        }&idloaitulieu=${idLoaiTuLieu}&keyword=${keyword}&page=${page}&size=${pageSize}&properties_sort=id&type_sort=desc`,
        method: "GET",
      });

      if (response?.data) {
        const { content, totalElements } = response.data;
        const formattedDocuments = content.map((doc: any) => ({
          idTulieu: doc.id,
          maDanToc: doc.maDanToc,
          idNhomTuLieu: doc.idNhomTuLieu,
          idLoaiTuLieu: doc.idLoaiTuLieu,
          anhDaiDien: doc.anhDaiDien,
          isTuLieu: doc.isTuLieu,
          isVideo: doc.isVideo,
          isHinhAnh: doc.isHinhAnh,
          tenTuLieu: doc.tenTuLieu,
          historicalPeriod: doc.loaiTuLieu.tenLoaiTuLieu || "",
          moTa: doc.moTa,
          tacGia: doc.tacGia,
          ngayDang: doc.ngayDang,
          ngayBatDauLT: doc.ngayBatDauLT,
          ngayKetThucLT: doc.ngayKetThucLT,
          trangThai: doc.trangThai,
          isSuDung: doc.isSuDung,
        }));

        setDocuments(formattedDocuments);
        setPagination((prev) => ({
          ...prev,
          current: page,
          total: totalElements,
        }));
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistoricalDocuments(
      pagination.current,
      pagination.pageSize,
      searchParams.nhom || idNhomTuLieuState,
      searchParams.loai,
      searchParams.keyword
    );
  }, [
    pagination.current,
    pagination.pageSize,
    searchParams.nhom,
    searchParams.loai,
    searchParams.keyword
  ]);

  const handleSearch = (values: any) => {
    setSearchParams({
      nhom: values.nhom || idNhomTuLieuState,
      loai: values.loai || "",
      keyword: values.keyword || "",
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
  };

  return (
    <div className="QTI-content-detail">
      <Title level={4} style={{ textAlign: "left" }}>
        {tenNhomTuLieu ? (
          <>
            <DatabaseOutlined /> Tư liệu{" "}
            <ArrowRightOutlined style={{ margin: "0 8px", fontSize: "10px" }} />{" "}
            {tenNhomTuLieu}
          </>
        ) : (
          <>
            <DatabaseOutlined /> Tư liệu
          </>
        )}
      </Title>
      <SearchForm handleSearch={handleSearch} />
      <List
        loading={loading}
        itemLayout="vertical"
        dataSource={documents}
        className="QTI-list-parent-content"
        locale={{ emptyText: "Không có dữ liệu" }}
        renderItem={(item) => (
          <List.Item key={item.idTulieu}>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Link to={`/trang-chu/tu-lieu/${item.idTulieu}/chi-tiet`}>
                  <img
                    width="100%"
                    alt={item.tenTuLieu}
                    src={item.anhDaiDien}
                    className="QTI-thumb-img-list"
                  />
                </Link>
              </Col>
              <Col xs={24} sm={12} md={16}>
                <List.Item.Meta
                  title={
                    <Link to={`/trang-chu/tu-lieu/${item.idTulieu}/chi-tiet`}>
                      <Title level={4}>{item.tenTuLieu}</Title>
                    </Link>
                  }
                  description={
                    <Space
                      size={16}
                      style={{ display: "flex", flexWrap: "wrap" }}
                    >
                      {item.tacGia && (
                        <Text strong style={{ fontSize: "12px" }}>
                          <UserOutlined /> {item.tacGia}
                        </Text>
                      )}
                      {item.ngayDang && (
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          <CalendarOutlined />{" "}
                          {new Date(item.ngayDang).toLocaleDateString("vi-VN")}
                        </Text>
                      )}
                      {item.historicalPeriod && (
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          <FolderOpenOutlined /> {item.historicalPeriod}
                        </Text>
                      )}
                    </Space>
                  }
                />
                <Paragraph
                  ellipsis={{ rows: 3, expandable: false }}
                  style={{ marginTop: 16 }}
                >
                  {item.moTa}
                </Paragraph>
                <Link to={`/trang-chu/tu-lieu/${item.idTulieu}/chi-tiet`}>
                  Xem thêm →
                </Link>
              </Col>
            </Row>
          </List.Item>
        )}
      />

      <div style={{ textAlign: "center", marginTop: 24 }}>
        <Pagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          showTotal={(total) =>
            pagination.pageSize <= total
              ? `Hiển thị ${pagination.pageSize} trong tổng ${total}`
              : `Tổng ${total}`
          }
          onChange={handlePageChange}
          showSizeChanger={false}
          showQuickJumper={false}
          align="end"
        />
      </div>
    </div>
  );
};

export default ParentTuLieu;
